import axiosInstance from './axiosInstance';

const baseUrl = `${SUPP_ADMIN_BASE_URL}/supp-management`;

/** 话题列表请求参数 */
export interface ArtistSpaceListParams {
  userNumber?: string | number;
  status?: number; // 1 上架中 0 已下架
  currentPage: number;
  pageSize: number;
}

/** 话题列表项（与接口返回字段一致） */
export interface ArtistSpaceTopic {
  artistSpaceId?: number;
  name?: string;
  cover?: string;
  promoterName?: string;
  userNumber?: string | number;
  createTime?: string;
  participantsNumber?: number;
  status?: number; // 1 上架中 0 已下架
  sort?: number;
}

/** 话题列表响应 */
export interface ArtistSpaceListResponse {
  total: number;
  currentPage: number;
  pageSize: number;
  records: ArtistSpaceTopic[];
}

/** 投稿作品列表请求参数（接口支持：currentPage、pageSize、artistSpaceId、status、userNumber） */
export interface ArtistSpaceArtworksParams {
  artistSpaceId: number;
  currentPage: number;
  pageSize: number;
  userNumber?: string | number;
  status?: number;
}

/** 投稿作品列表项（与 getArtworksById 接口返回字段一致） */
export interface ArtistSpaceArtwork {
  artworkUrl?: string | null;
  description?: string;
  username?: string;
  userNumber?: string | number;
  userId?: string;
  artworkId?: string;
  vipName?: string;
  likeCount?: number;
  commentCount?: number;
  createTime?: string;
  status?: number; // 1 发布成功 2 审核未通过
  /** 下架/上架时传此 id（若接口返回则用，否则可能需用 artworkId） */
  artistSpaceId?: number;
}

/** 投稿作品列表响应 */
export interface ArtistSpaceArtworksResponse {
  total: number;
  currentPage: number;
  pageSize: number;
  records: ArtistSpaceArtwork[];
}

/** 更新话题参数（上下架 | 修改排序） */
export interface ArtistSpaceUpdateParams {
  artistSpaceId: number;
  status?: number | string;
  sort?: number;
}

/** 投稿作品上下架参数：1 发布成功，2 审核未通过；artworkId、userId 为字符串 */
export interface UpdateArtworkParams {
  artworkId: string;
  userId: string;
  status: number; // 1 发布成功 2 审核未通过
}

interface ApiResponse<T> {
  status: number;
  success: boolean;
  message: string;
  data: T;
}

/**
 * 话题列表
 */
export const getArtistSpaceList = async (
  params: ArtistSpaceListParams
): Promise<ArtistSpaceListResponse> => {
  const res = await axiosInstance.post<ApiResponse<ArtistSpaceListResponse>>(
    `${baseUrl}/artistSpace/list`,
    params
  );
  if (res.data.status !== 200 || !res.data.success) {
    throw new Error(res.data.message || '获取话题列表失败');
  }
  return res.data.data;
};

/**
 * 根据话题ID获取投稿作品列表
 * 入参：currentPage、pageSize、artistSpaceId、status（1发布成功 2审核未通过）、userNumber（用户ID）
 */
export const getArtistSpaceArtworksById = async (
  params: ArtistSpaceArtworksParams
): Promise<ArtistSpaceArtworksResponse> => {
  const body: Record<string, number> = {
    currentPage: Number(params.currentPage) || 0,
    pageSize: Number(params.pageSize) || 0,
    artistSpaceId: Number(params.artistSpaceId) || 0,
  };
  if (params.status !== undefined && params.status !== null && params.status !== '') {
    body.status = Number(params.status);
  }
  if (params.userNumber !== undefined && params.userNumber !== null && params.userNumber !== '') {
    body.userNumber = Number(params.userNumber);
  }
  const res = await axiosInstance.post<ApiResponse<ArtistSpaceArtworksResponse>>(
    `${baseUrl}/artistSpace/getArtworksById`,
    body as Record<string, unknown>
  );
  if (res.data.status !== 200 || !res.data.success) {
    throw new Error(res.data.message || '获取投稿作品列表失败');
  }
  return res.data.data;
};

/**
 * 更新话题（上下架 | 修改排序）
 */
export const updateArtistSpace = async (
  params: ArtistSpaceUpdateParams
): Promise<void> => {
  const res = await axiosInstance.post<ApiResponse<unknown>>(
    `${baseUrl}/artistSpace/update`,
    params
  );
  if (res.data.status !== 200 || !res.data.success) {
    throw new Error(res.data.message || '更新失败');
  }
};

/**
 * 投稿作品上下架
 * POST /supp-management/artistSpace/updateArtwork
 * @param params.artworkId 作品id
 * @param params.userId 用户 id
 * @param params.status 1 发布成功 2 审核未通过
 */
export const updateArtistSpaceArtwork = async (
  params: UpdateArtworkParams
): Promise<void> => {
  const res = await axiosInstance.post<ApiResponse<unknown>>(
    `${baseUrl}/artistSpace/updateArtwork`,
    {
      artworkId: params.artworkId,
      userId: params.userId,
      status: Number(params.status),
    }
  );
  if (res.data.status !== 200 || !res.data.success) {
    throw new Error(res.data.message || '更新失败');
  }
};
